import { PostcodeModel } from './PostcodeModel';
import { HybrisAddressResponseModel } from 'models/HybrisResponses';
import { BasketAddressModel } from 'models/Basket';
import { NUMBER_VALID_MASK } from 'helpers';
import { CustexAddressTypes, CustexAddressModel } from './CustexAddressModel';

const MAX_HOUSE_LENGTH = 10;

export class AddressModel {
    public flat: string | null = null;
    public building: string | null = null;
    public number: string | null = null;
    public street: string | null = null;
    public town: string | null = null;
    public locality: string | null = null;
    public county: string | null = null;
    public postcode: PostcodeModel | null = null;

    constructor(
        flat: string | null,
        building: string | null,
        number: string | null,
        street: string | null,
        town: string | null,
        locality: string | null,
        county: string | null,
        postcode: string | null
    ) {
        this.flat = flat ? flat.trim() : flat;
        this.building = building ? building.trim() : building;
        this.number = number ? number.trim() : number;
        this.street = street ? street.trim() : street;
        this.town = town ? town.trim() : town;
        this.locality = locality ? locality.trim() : locality;
        this.county = county ? county.trim() : county;
        this.postcode = postcode ? new PostcodeModel(postcode) : null;
    }

    public toString(): string {
        let buildingLine = (this.flat && this.building) ? `${this.flat} ${this.building}` : this.flat || this.building;
        let streetLine = (this.number && this.street) ? `${this.number} ${this.street}` : this.number || this.street;

        buildingLine = buildingLine ? buildingLine + ', ' : '';
        streetLine = streetLine ? streetLine + ', ' : '';
        let town = this.town ? this.town + ', ' : '';
        let county = this.county ? this.county + ', ' : '';
        let postcode = this.postcode ? this.postcode.code : '';

        return [
            this.getStringWithProperCase(buildingLine),
            this.getStringWithProperCase(streetLine),
            this.getStringWithProperCase(town),
            this.getStringWithProperCase(county),
            postcode
        ].join('');
    }

    public isEmptyAddress(): boolean {
        return !this.postcode?.code;
    }

    private getStringWithProperCase(value: string): string {
        return value.replace(/\w\S*/g, (x: string) => x[0].toUpperCase() + x.slice(1).toLowerCase());
    }
}

export function createEmptyAddress(): AddressModel {
    return new AddressModel(null, null, null, null, null, null, null, null);
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function convertHybrisAddressResponseToAddressModel(source: HybrisAddressResponseModel | BasketAddressModel): AddressModel | null {
    if (source.line1 && source.town) {
        let street = null;
        let flat = null;
        street = source.line1;
        let parts = /^\s*(\d+\w?),\s+(.*)$/.exec(source.line1);
        if (parts) {
            flat = parts[1];
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            street = parts[2];
        }

        if (source.building && source.building.length > 0) {
            street = street.replace(`${source.building} `, '');
        }

        // TODO: fix problem with different postode formats in hybris responses inside basket and on direct hybris call
        let postalCode = source instanceof BasketAddressModel ? source.postcode.code : source.postalCode;

        const isHouseNumber = source.building
            && source.building.length > 0
            && source.building.length <= MAX_HOUSE_LENGTH
            && (new RegExp(NUMBER_VALID_MASK)).test(source.building);

        return new AddressModel(
            source.apartment && source.apartment.length > 0 ? source.apartment : flat,
            source.building && !isHouseNumber ? source.building : null,
            source.building && isHouseNumber ? source.building : null,
            street,
            source.town && source.town.length > 0 ? source.town : null,
            null,
            source.district && source.district.length > 0 ? source.district : null,
            postalCode && postalCode.length > 0 ? postalCode : null
        );
    }

    return null;
}

export function areAddressesEqual(a: AddressModel | null, b: AddressModel | null): boolean {
    if (!a && b) {
        return false;
    }

    if (a && !b) {
        return false;
    }

    if (!a && !b) {
        return true;
    }

    return a!.flat === b!.flat
        && a!.building === b!.building
        && a!.number === b!.number
        && a!.street === b!.street
        && a!.town === b!.town
        && a!.locality === b!.locality
        && a!.county === b!.county
        && a!.postcode?.code === b!.postcode?.code;
}

export function convertAddressToCustexAddress(address: AddressModel, addressId: number | undefined, addressType: CustexAddressTypes): CustexAddressModel {
    let houseNumber = address.number || address.building || '';

    let line1 = address.street!;
    if (!line1.includes(houseNumber)) {
        line1 = houseNumber + ' ' + line1;
    }

    if (address.flat && !line1.includes(address.flat)) {
        line1 = address.flat + ', ' + line1;
    }

    return {
        id: addressId,
        addressTypeId: addressType,
        houseNameNumber: houseNumber,
        line1: line1,
        line2: address.town!,
        line3: address.county!,
        line4: '',
        postCode: address.postcode!.code
    };
}